import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Calendar, 
  Tag, 
  Package, 
  Building2, 
  Percent, 
  Truck, 
  Gift, 
  Star, 
  AlertCircle, 
  ExternalLink,
  AlertTriangle,
  Hash,
  ShoppingCart,
  PackageMinus,
  PackagePlus,
  Layers
} from 'lucide-react';
import { isPartnerClickable, PARTNER_DISABLED_TOOLTIP, PARTNER_LABEL_INACTIVE } from '../lib/partnerPolicy';

/**
 * RetailerOfferView V4.6 REDESIGN
 * 
 * STRUCTURE:
 * 1. BANNIÈRE HERO (image pleine largeur 21:9)
 * 2. TITRE OFFRE (typographie dominante)
 * 3. PARTENAIRE (bloc identifiable)
 * 4. DESCRIPTION (zone de lecture naturelle)
 * 5. CONDITIONS (tableau structuré B2B)
 * 6. PRODUITS CONCERNÉS
 */
const RetailerOfferView = ({ 
  offer, 
  products = [], 
  partner = null, 
  mode = 'live',
  productLoadingCase = null,
  onRetry = null
}) => {
  
  const isPreview = mode === 'preview';
  
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date)) return null;
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return dateString; }
  };

  const getTypeInfo = (type) => {
    const types = {
      remise: { icon: Percent, label: 'Remise', color: 'bg-emerald-500' },
      franco: { icon: Truck, label: 'Franco', color: 'bg-blue-500' },
      goodies: { icon: Gift, label: 'Goodies', color: 'bg-violet-500' },
      gratuite: { icon: Gift, label: 'Gratuité', color: 'bg-pink-500' },
      mixte: { icon: Star, label: 'Mixte', color: 'bg-amber-500' }
    };
    return types[type] || { icon: Tag, label: 'Offre', color: 'bg-gray-500' };
  };
  
  const getStatusInfo = (status) => {
    const statuses = {
      active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
      draft: { label: 'Brouillon', color: 'bg-orange-100 text-orange-700' },
      archived: { label: 'Archivée', color: 'bg-gray-100 text-gray-600' }
    };
    return statuses[status] || statuses.draft;
  };

  const ConditionalLink = ({ to, children, className = '' }) => {
    if (isPreview) return <span className={`${className} cursor-default`} onClick={(e) => e.preventDefault()}>{children}</span>;
    return <Link to={to} className={className}>{children}</Link>;
  };

  if (!offer) return null;

  const typeInfo = getTypeInfo(offer.type);
  const statusInfo = getStatusInfo(offer.status);
  const TypeIcon = typeInfo.icon;
  const startDate = formatDate(offer.start_date);
  const endDate = formatDate(offer.end_date);
  
  // Conditions tableau — ordre strict
  const conditionRows = [
    (startDate || endDate) && { icon: Calendar, label: 'Période de validité', value: startDate && endDate ? `Du ${startDate} au ${endDate}` : startDate ? `À partir du ${startDate}` : `Jusqu'au ${endDate}` },
    offer.discount_percent && { icon: Percent, label: 'Remise', value: `-${offer.discount_percent}%` },
    offer.gratuite_description && { icon: Gift, label: 'Gratuité', value: offer.gratuite_description },
    offer.minimum_order && { icon: PackageMinus, label: 'Minimum de commande', value: `${offer.minimum_order}${offer.min_order_unit || '€'}` },
    offer.maximum_order && { icon: PackagePlus, label: 'Maximum de commande', value: `${offer.maximum_order}${offer.max_order_unit || '€'}` },
    offer.multiple_order && { icon: Layers, label: 'Multiple de commande', value: `${offer.multiple_order}` },
    offer.franco_threshold && { icon: Truck, label: 'Franco de port', value: `${offer.franco_threshold}€` },
    offer.goodies_description && { icon: Gift, label: 'Goodies', value: offer.goodies_description },
  ].filter(Boolean);

  return (
    <div className="w-full bg-[#F9F9F7] min-h-screen" data-testid="retailer-offer-view" data-mode={mode}>
      
      {/* ══════════ AVERTISSEMENT PREVIEW ══════════ */}
      {isPreview && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <p className="text-sm text-amber-700"><span className="font-medium">Prévisualisation</span> — Navigation désactivée</p>
            <Badge className={`${statusInfo.color} ml-auto text-xs`}>{statusInfo.label}</Badge>
          </div>
        </div>
      )}

      {/* ══════════ 1. BANNIÈRE HERO (bandeau commercial premium) ══════════ */}
      <section className="w-full bg-gray-100" data-testid="offer-hero-banner">
        {offer.image_url ? (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-4">
            <div className="relative overflow-hidden rounded-lg bg-gray-900" style={{ minHeight: '160px', maxHeight: '360px' }}>
              {/* Fond flou dérivé de l'image — remplissage discret */}
              <img
                src={offer.image_url}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-30"
              />
              {/* Image principale — ratio préservé, pas de déformation */}
              <img
                src={offer.image_url}
                alt={offer.title || 'Image offre'}
                className="relative w-full h-full object-contain"
                style={{ maxHeight: '360px' }}
                data-testid="offer-image"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              {/* Overlay léger — sobriété B2B */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent pointer-events-none"></div>
              {/* Badge promo overlay */}
              {(offer.discount_percent || offer.badge_text) && (
                <div 
                  className="absolute top-3 left-3 px-3 py-1.5 rounded-md text-white font-bold text-sm shadow"
                  style={{ backgroundColor: offer.badge_color || '#dc2626' }}
                  data-testid="offer-badge"
                >
                  {offer.badge_text || `-${offer.discount_percent}%`}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-4">
            <div className={`relative aspect-[3/1] sm:aspect-[4/1] ${typeInfo.color} flex items-center justify-center overflow-hidden rounded-lg`}>
              <TypeIcon className="h-12 w-12 text-white/20" />
              {(offer.discount_percent || offer.badge_text) && (
                <div className="absolute top-3 left-3 px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-md text-white font-bold text-sm" data-testid="offer-badge">
                  {offer.badge_text || `-${offer.discount_percent}%`}
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* ══════════ CONTENU PRINCIPAL ══════════ */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* ══════════ 2. TITRE OFFRE ══════════ */}
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight" data-testid="offer-title">
          {offer.title}
        </h1>

        {/* ══════════ 3. PARTENAIRE ══════════ */}
        {partner && (
          <div className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm" data-testid="offer-partner">
            {partner.logo && (
              <div className="w-14 h-14 rounded-lg bg-gray-50 border border-gray-200 p-2 flex items-center justify-center flex-shrink-0">
                <img src={partner.logo} alt={partner.nom} className="max-w-full max-h-full object-contain" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Proposé par</p>
              <p className="font-semibold text-gray-900 text-lg">{partner.nom}</p>
            </div>
            {!isPreview && isPartnerClickable(partner) && (
              <ConditionalLink to={`/partner/${partner.slug || partner.id}`}>
                <Button variant="outline" size="sm">
                  <Building2 className="h-4 w-4 mr-2" />
                  Voir la fiche partenaire
                </Button>
              </ConditionalLink>
            )}
            {!isPreview && !isPartnerClickable(partner) && (
              <span className="text-xs text-gray-400 italic" title={PARTNER_DISABLED_TOOLTIP}>{PARTNER_LABEL_INACTIVE}</span>
            )}
          </div>
        )}

        {/* ══════════ 4. DESCRIPTION ══════════ */}
        {offer.description && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-5" data-testid="offer-description-section">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Description de l'offre</h2>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line" data-testid="offer-description">
              {offer.description}
            </p>
            {offer.offer_url && !isPreview && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <a href={offer.offer_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm">
                  Voir les conditions détaillées <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            )}
          </div>
        )}

        {/* ══════════ 5. CONDITIONS — TABLEAU STRUCTURÉ ══════════ */}
        {conditionRows.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-testid="offer-conditions-table">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                <ShoppingCart className="h-4 w-4 text-gray-400" /> Conditions de l'offre
              </h2>
            </div>
            <div>
              {conditionRows.map((row, idx) => {
                const IconComp = row.icon;
                return (
                  <div key={idx} className={`py-3 px-4 flex items-center gap-3 text-sm ${idx < conditionRows.length - 1 ? 'border-b border-gray-100' : ''}`}>
                    <IconComp className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-500 w-44 flex-shrink-0">{row.label}</span>
                    <span className="font-medium text-gray-900">{row.value}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ══════════ 6. PRODUITS CONCERNÉS ══════════ */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
              <Package className="h-4 w-4 text-gray-400" />
              Produits concernés
              {products.length > 0 && <span className="text-gray-400 font-normal">({products.length})</span>}
            </h2>
          </div>
          <div className="p-4">
            {offer.apply_to_all_products && (
              <div className="text-center py-4 bg-emerald-50 rounded-lg border border-emerald-100 mb-4" data-testid="offer-all-products">
                <Package className="h-6 w-6 text-emerald-500 mx-auto mb-1" />
                <p className="font-medium text-emerald-700 text-sm">
                  Cette offre s'applique à tous les produits {partner?.nom ? `de ${partner.nom}` : ''}
                </p>
              </div>
            )}

            {products.length > 0 ? (
              <div data-testid="offer-products-grid">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {products.map(product => (
                    <ConditionalLink key={product.id} to={`/dashboard/produit/${product.id}`} className="block group">
                      <div className={`rounded-lg overflow-hidden bg-white border border-gray-100 ${!isPreview ? 'hover:border-gray-300 hover:shadow-sm transition-all' : ''}`}>
                        {(product.visuels?.[0] || product.images?.[0]) ? (
                          <div className="w-full aspect-square bg-gray-50 flex items-center justify-center p-2">
                            <img src={product.visuels?.[0] || product.images?.[0]} alt={product.intitule || product.name} className="max-w-full max-h-full object-contain" />
                          </div>
                        ) : (
                          <div className="w-full aspect-square bg-gray-50 flex items-center justify-center">
                            <Package className="h-8 w-8 text-gray-200" />
                          </div>
                        )}
                        <div className="p-2.5">
                          <p className={`text-sm font-medium text-gray-900 line-clamp-2 ${!isPreview ? 'group-hover:text-blue-600' : ''}`}>
                            {product.intitule || product.name}
                          </p>
                          {(product.prix_vente_conseille || product.prix_vente) && (
                            <p className="text-xs text-gray-400 mt-0.5">{product.prix_vente_conseille || product.prix_vente}€</p>
                          )}
                        </div>
                      </div>
                    </ConditionalLink>
                  ))}
                </div>
              </div>
            ) : productLoadingCase === 'error' ? (
              <div className="text-center py-8" data-testid="offer-products-error">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 text-red-400" />
                <p className="text-red-600 font-medium text-sm mb-2">Erreur de chargement</p>
                {onRetry && !isPreview && <Button variant="outline" size="sm" onClick={onRetry}>Réessayer</Button>}
              </div>
            ) : productLoadingCase === 'no_products' || (!offer.apply_to_all_products && products.length === 0) ? (
              <div className="text-center py-8" data-testid="offer-no-products-config">
                <div className="bg-gray-50 rounded-lg p-4 max-w-xs mx-auto">
                  <Package className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm text-gray-500">{isPreview ? 'Aucun produit associé' : 'Aucun produit pour cette offre'}</p>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
};

export default RetailerOfferView;
